import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Smartphone, Pencil, FileText, Wrench, Cpu } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  useAccountMembersForPicker,
  useAccountContactRoles,
  useUpdateMemberTags,
} from '../hooks/useSubscriptionManagers';
import type { UsergroupMemberDetail } from '../types';
import type { ContactRoleEntry } from '../api';

const ROLES = [
  {
    tag: 'Contractual Manager',
    label: 'Contractual',
    short: 'CM',
    Icon: FileText,
    roleKey: 'contractual_manager' as const,
  },
  {
    tag: 'Technical Manager',
    label: 'Technical',
    short: 'Tech',
    Icon: Wrench,
    roleKey: 'technical_manager' as const,
  },
  {
    tag: 'IT Manager',
    label: 'IT',
    short: 'IT',
    Icon: Cpu,
    roleKey: 'it_manager' as const,
  },
] as const;

type RoleDef = (typeof ROLES)[number];

function memberLabel(m: UsergroupMemberDetail): string {
  const name = [m.firstname, m.lastname].filter(Boolean).join(' ').trim();
  return name || m.email || 'Unknown';
}

function contactLabel(c: ContactRoleEntry): string {
  const name = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
  return name || c.email || 'Unknown';
}

function RolePicker({
  role,
  members,
  selectedMemberId,
  onSelect,
  disabled,
}: {
  role: RoleDef;
  members: UsergroupMemberDetail[];
  selectedMemberId: string | null;
  onSelect: (memberId: string | null) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const filtered = members.filter((m) => {
    if (!search) return true;
    return memberLabel(m).toLowerCase().includes(search.toLowerCase());
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
          disabled={disabled}
          aria-label={`Edit ${role.label} Manager`}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="end">
        <div className="border-b p-2">
          <Input
            placeholder="Filter contacts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-7 text-xs"
          />
        </div>
        <div className="max-h-56 overflow-y-auto p-1">
          <button
            type="button"
            className={`w-full text-left px-2 py-1.5 text-xs rounded hover:bg-accent ${
              !selectedMemberId ? 'font-medium' : 'text-muted-foreground'
            }`}
            onClick={() => {
              onSelect(null);
              setOpen(false);
            }}
          >
            (none)
          </button>
          {filtered.map((member) => {
            const isSelected = member.id === selectedMemberId;
            return (
              <button
                type="button"
                key={member.id}
                className={`w-full text-left px-2 py-1.5 text-xs rounded hover:bg-accent ${
                  isSelected ? 'bg-accent font-medium' : ''
                }`}
                onClick={() => {
                  onSelect(member.id);
                  setOpen(false);
                }}
              >
                <div className="truncate">{memberLabel(member)}</div>
                {member.role && (
                  <div className="truncate text-[10px] text-muted-foreground">{member.role}</div>
                )}
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div className="px-2 py-3 text-xs text-muted-foreground text-center">
              {members.length === 0 ? 'No contacts on this account' : 'No matching contacts'}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ContactCard({ contact }: { contact: ContactRoleEntry }) {
  const name = contactLabel(contact);
  return (
    <div className="min-w-0 flex-1">
      <div className="flex items-baseline gap-1.5">
        {contact.id ? (
          <Link
            to={`/crm/contacts/${contact.id}`}
            className="truncate text-sm font-medium hover:underline text-atek-blue"
          >
            {name}
          </Link>
        ) : (
          <span className="truncate text-sm font-medium text-foreground">{name}</span>
        )}
        {contact.title && (
          <span className="truncate text-[11px] text-muted-foreground">· {contact.title}</span>
        )}
      </div>
      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
        {contact.email && (
          <a
            href={`mailto:${contact.email}`}
            className="inline-flex items-center gap-1 hover:text-atek-blue"
            title={contact.email}
          >
            <Mail className="h-3 w-3" />
            <span className="truncate max-w-[160px]">{contact.email}</span>
          </a>
        )}
        {contact.phone && (
          <a
            href={`tel:${contact.phone}`}
            className="inline-flex items-center gap-1 hover:text-atek-blue"
            title={`Office: ${contact.phone}`}
          >
            <Phone className="h-3 w-3" />
            <span>{contact.phone}</span>
          </a>
        )}
        {contact.mobile_phone && (
          <a
            href={`tel:${contact.mobile_phone}`}
            className="inline-flex items-center gap-1 hover:text-atek-blue"
            title={`Mobile: ${contact.mobile_phone}`}
          >
            <Smartphone className="h-3 w-3" />
            <span>{contact.mobile_phone}</span>
          </a>
        )}
      </div>
    </div>
  );
}

export function KeyContactsPanel({ accountId }: { accountId: string }) {
  // Display reads contact_roles (canonical surface) — same source the accounts
  // table uses, so the panel and the table always agree on who is CM / TM / IT.
  // The picker (RolePicker) still chooses from usergroup_members and the write
  // path is PATCH /v4/accounts/:id/usergroup-members/tags, which mirrors to
  // contact_roles inside the DAO transaction. Contacts in contact_roles who
  // aren't usergroup_members appear in the display but can't be removed from
  // the picker — a future PR adds a direct contact-role write endpoint.
  // see: apps/rest_server/docs/identity-model.md#contact-roles
  const { data: contactRoles } = useAccountContactRoles(accountId);
  const { data: members } = useAccountMembersForPicker(accountId);
  const tagsMutation = useUpdateMemberTags(accountId);

  const memberList = members || [];

  return (
    <Card className="w-full md:w-[360px]">
      <CardContent className="p-3 space-y-2.5">
        {ROLES.map((role) => {
          const assignedContacts = contactRoles?.[role.roleKey] ?? [];
          const primary = assignedContacts[0] || null;
          const extraCount = Math.max(0, assignedContacts.length - 1);

          // Picker selection — try to match the primary contact to a usergroup_member by email.
          // If the contact isn't a member (CRM-only), the picker shows "(none)" but the display
          // still renders the contact card from contact_roles.
          const selectedMember = primary?.email
            ? memberList.find((m) => m.email && m.email.toLowerCase() === primary.email?.toLowerCase())
            : null;

          return (
            <div key={role.tag} className="flex items-start gap-2">
              <div className="mt-0.5 flex h-5 w-12 shrink-0 items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                <role.Icon className="h-3 w-3" />
                <span>{role.short}</span>
              </div>
              {primary ? (
                <div className="min-w-0 flex-1">
                  <ContactCard contact={primary} />
                  {extraCount > 0 && (
                    <div className="mt-1 text-[10px] text-muted-foreground italic">
                      +{extraCount} more
                    </div>
                  )}
                </div>
              ) : (
                <div className="min-w-0 flex-1 text-xs italic text-muted-foreground">
                  Not set
                </div>
              )}
              <RolePicker
                role={role}
                members={memberList}
                selectedMemberId={selectedMember?.id || null}
                onSelect={(memberId) =>
                  tagsMutation.mutate({
                    tag: role.tag,
                    memberIds: memberId ? [memberId] : [],
                  })
                }
                disabled={tagsMutation.isPending}
              />
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
